import * as React from 'react';
import {
    CommandBar,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    ICommandBarItemProps,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    AgentTaskQuestion,
    AgentTaskQuestionView,
    PAGE_SIZE_OPTIONS,
    PageShell,
    parseOptionalInt,
    updateSearchParams,
    useDocumentTitle,
} from '../shared';
import { getAgentTaskQuestions, resolveAgentTaskQuestions } from '../questionsApi';
import { TaskDetailPanel } from '../panels/TaskDetailPanel';
import {
    AgentTaskQuestionCollectionActions,
    AgentTaskQuestionEditor,
    useAgentTaskQuestionCollectionEditor,
} from '../questions/AgentTaskQuestionComponents';

interface TaskQuestionGroup {
    key: string;
    taskListId: number;
    taskId: number;
    snapshotId?: number;
    scope: string;
    questions: AgentTaskQuestion[];
}

export function groupAgentTaskQuestions(questions: AgentTaskQuestion[]): TaskQuestionGroup[] {
    const groups = new Map<string, TaskQuestionGroup>();
    questions.forEach(question => {
        const key = `${question.taskListId}:${question.taskId}`;
        const existing = groups.get(key);
        if (existing) {
            existing.questions.push(question);
        } else {
            groups.set(key, {
                key,
                taskListId: question.taskListId,
                taskId: question.taskId,
                snapshotId: question.snapshotId ?? undefined,
                scope: question.taskScope,
                questions: [question],
            });
        }
    });
    return Array.from(groups.values());
}

export function AgentTaskQuestionsPage() {
    useDocumentTitle('Clarification questions');
    const navigate = useNavigate();
    const { serviceName } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const view: AgentTaskQuestionView = searchParams.get('view') === 'history' ? 'history' : 'active';
    const pageNumber = Math.max(0, parseOptionalInt(searchParams.get('pageNumber')) ?? 0);
    const pageSize = Math.max(1, parseOptionalInt(searchParams.get('pageSize')) ?? 25);
    const taskListId = parseOptionalInt(searchParams.get('taskListId'));
    const taskId = parseOptionalInt(searchParams.get('taskId'));
    const workItemId = parseOptionalInt(searchParams.get('workItemId'));
    const [questions, setQuestions] = React.useState<AgentTaskQuestion[]>([]);
    const [totalCount, setTotalCount] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedTask, setSelectedTask] = React.useState<TaskQuestionGroup | null>(null);
    const questionEditor = useAgentTaskQuestionCollectionEditor(questions);

    const loadQuestions = React.useCallback(async () => {
        if (!serviceName) return;
        setLoading(true);
        setError(null);
        try {
            const result = await getAgentTaskQuestions(serviceName, {
                view,
                pageNumber,
                pageSize,
                taskListId,
                taskId,
                workItemId,
            });
            setQuestions(result.questions.filter(question => view === 'active' ? !question.isResolved : question.isResolved));
            setTotalCount(result.totalCount);
            questionEditor.resetDrafts();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load clarification questions.');
            setQuestions([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [pageNumber, pageSize, questionEditor.resetDrafts, serviceName, taskId, taskListId, view, workItemId]);

    React.useEffect(() => {
        void loadQuestions();
    }, [loadQuestions]);

    const groups = React.useMemo(() => groupAgentTaskQuestions(questions), [questions]);
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const filterDescription = [
        taskListId !== undefined ? `task list #${taskListId}` : null,
        taskId !== undefined ? `task #${taskId}` : null,
        workItemId !== undefined ? `work item #${workItemId}` : null,
    ].filter(Boolean).join(', ');

    async function submitChangedAnswers() {
        if (!serviceName || questionEditor.changedQuestionIds.length === 0) return;
        const resolutions = questionEditor.getChangedResolutions();
        if (resolutions.some(item => !item.declined && !item.answer)) {
            setError('Enter an answer or decline each changed question.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await resolveAgentTaskQuestions(serviceName, resolutions);
            if (view === 'active') {
                const resolvedIds = new Set(resolutions.map(item => item.questionId));
                setQuestions(current => current.filter(question => !resolvedIds.has(question.questionId)));
                setTotalCount(current => Math.max(0, current - resolvedIds.size));
            } else {
                await loadQuestions();
            }
            questionEditor.resetDrafts();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to resolve clarification questions.');
        } finally {
            setSaving(false);
        }
    }

    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'back',
            text: 'Back to task lists',
            iconProps: { iconName: 'Back' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks`),
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => void loadQuestions(),
        },
    ];

    function renderAnswerActions() {
        if (view !== 'active') return null;
        return (
            <AgentTaskQuestionCollectionActions
                editor={questionEditor}
                saving={saving}
                onSubmit={() => void submitChangedAnswers()}
            />
        );
    }

    return (
        <PageShell title="Clarification questions">
            <CommandBar items={commandItems} />
            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 12 }}>
                <div>
                    <Pivot
                        selectedKey={view}
                        onLinkClick={item => updateSearchParams(searchParams, setSearchParams, {
                            view: item?.props.itemKey === 'history' ? 'history' : null,
                            pageNumber: 0,
                        })}
                    >
                        <PivotItem headerText="Active" itemKey="active" />
                        <PivotItem headerText="History" itemKey="history" />
                    </Pivot>
                    {filterDescription && <Text>Filtered to {filterDescription}.</Text>}
                </div>
                <Stack horizontal verticalAlign="end" wrap tokens={{ childrenGap: 8 }}>
                    <div style={{ minWidth: 120 }}>
                        <Dropdown
                            label="Page size"
                            options={PAGE_SIZE_OPTIONS as IDropdownOption[]}
                            selectedKey={pageSize}
                            onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                pageSize: Number(option?.key),
                                pageNumber: 0,
                            })}
                        />
                    </div>
                    {renderAnswerActions()}
                </Stack>
            </Stack>

            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading clarification questions..." />
            ) : groups.length === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    {view === 'active' ? 'There are no open clarification questions.' : 'No question history was found.'}
                </MessageBar>
            ) : (
                <Stack tokens={{ childrenGap: 16 }}>
                    {groups.map(group => (
                        <section key={group.key} style={{ border: '1px solid #d2d0ce', borderRadius: 6, overflow: 'hidden' }}>
                            <button
                                type="button"
                                onClick={() => setSelectedTask(group)}
                                style={{
                                    width: '100%',
                                    padding: 12,
                                    border: 0,
                                    background: '#f3f2f1',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                }}
                            >
                                <Text variant="large">{group.scope}</Text>
                                <div><Text variant="small">Task #{group.taskId} • Task list #{group.taskListId} • {group.questions.length} question(s)</Text></div>
                            </button>
                            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: 16 } }}>
                                {group.questions.map(question => {
                                    return (
                                        <AgentTaskQuestionEditor
                                            key={question.questionId}
                                            question={question}
                                            draft={questionEditor.getDraft(question)}
                                            readOnly={view === 'history'}
                                            onChange={patch => questionEditor.updateDraft(question, patch)}
                                        />
                                    );
                                })}
                            </Stack>
                        </section>
                    ))}
                </Stack>
            )}

            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                        text="Previous"
                        disabled={pageNumber <= 0}
                        onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: pageNumber - 1 })}
                    />
                    <DefaultButton
                        text="Next"
                        disabled={pageNumber + 1 >= totalPages}
                        onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: pageNumber + 1 })}
                    />
                    <Text>Page {pageNumber + 1} of {totalPages} • {totalCount} question(s)</Text>
                </Stack>
                {renderAnswerActions()}
            </Stack>

            {serviceName && selectedTask && (
                <TaskDetailPanel
                    isOpen={true}
                    serviceName={serviceName}
                    taskListId={selectedTask.taskListId}
                    taskId={selectedTask.taskId}
                    snapshotId={selectedTask.snapshotId}
                    onDismiss={() => setSelectedTask(null)}
                />
            )}
        </PageShell>
    );
}
